import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

interface FileObject {
  siteId: number;
  file: File;
}

interface FilePreviewModalProps {
  files: FileObject[];
  text: string;
  fileAddedCallback: (fileObject: FileObject) => void; // Accept callback prop
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ files, text }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [csvContent, setCsvContent] = useState<string[][]>([]);
  const tableRef = useRef<HTMLTableElement>(null);

  const intl = useIntl();

  useEffect(() => {
    const file = files[currentIndex];
    if (file && file.file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const textInFile = e.target?.result as string;
        const rows = textInFile.split('\n').map((row) => row.split(',')); // Simple CSV parsing
        setCsvContent(rows);
      };
      reader.readAsText(file.file);
    }
  }, [currentIndex, files]);

  const handleSlide = (direction: 'prev' | 'next') => {
    setCurrentIndex((prev) => {
      if (direction === 'prev') {
        return prev === 0 ? files.length - 1 : prev - 1;
      }
      return prev === files.length - 1 ? 0 : prev + 1;
    });
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (files.length === 0) return <p>{intl.formatMessage({ id: 'noCSVFilesToPreview' })}</p>;

  return (
    <div>
      <h3>{text}</h3>
      <p>
        {' '}
        {files[currentIndex].file.name} (Site ID: {files[currentIndex].siteId})
      </p>
      {files.length > 1 && (
        <div className="preview-button-container" style={{ overflow: 'none' }}>
          <button className="btn btn-secondary button" onClick={() => handleSlide('prev')}>
            <i className="icon icon-chevron-left " />
            {intl.formatMessage({ id: 'previousFile' })}
          </button>
          <span className="page-indicator">
            {currentIndex + 1} / {files.length}
          </span>
          <button className="btn btn-secondary button" onClick={() => handleSlide('next')}>
            {intl.formatMessage({ id: 'nextFile' })}
            <i className="icon icon-chevron-right" />
          </button>
        </div>
      )}
      <div
        id="file-preview-table"
        className={
          files.length > 1 ? 'file-preview-with-table with-buttons' : 'file-preview-with-table'
        }
      >
        {csvContent.length > 0 ? (
          <table className="w-100" ref={tableRef}>
            <thead>
              <tr>
                {csvContent[0].map((header, index) => (
                  <th key={index} style={{ border: '1px solid #ddd', padding: '5px' }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {csvContent.slice(1).map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} style={{ border: '1px solid #ddd', padding: '5px' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{intl.formatMessage({ id: 'loadingCSVContent' })}</p>
        )}
      </div>
    </div>
  );
};

export default FilePreviewModal;
